import React, { useState } from "react";

import clsx from "clsx";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface DescriptionPreviewProps {
  name: string;
}

const looksLikeHtml = (value?: string) =>
  !!value && /<\/?[a-z][a-z0-9]*[\s>/]/i.test(value);

/**
 * A meeting that came from Outlook carries the invitation body as a whole HTML
 * document — a Teams invite is a page of nested divs and inline styles — and a
 * textarea can only show that as source.
 *
 * The value is read through `useController`, the same hook the shared TextArea
 * uses, so the field is registered and re-rendered exactly the way every other
 * field on this form is.
 *
 * It is rendered in a sandboxed iframe rather than inserted into this page. The
 * body is written by whoever created the calendar event, which includes people
 * outside the company, so it is untrusted input: without `allow-scripts` the
 * browser refuses to run anything inside the frame, and the frame has no access
 * to the CMS around it. `allow-popups` is there only so the join link can open
 * in a new tab.
 *
 * Anything that is not HTML — a description typed in the CMS — keeps the plain
 * textarea it always had.
 */
const DescriptionPreview = ({ name }: DescriptionPreviewProps) => {
  const { t } = useTranslation(["meeting", "common"]);

  const {
    field,
    fieldState: { error },
  } = useController({ name });

  const [showSource, setShowSource] = useState(false);

  const value = typeof field.value === "string" ? field.value : "";

  const textarea = (
    <textarea
      rows={showSource ? 12 : 5}
      {...field}
      value={value}
      className={clsx("form-control", { "is-invalid": !!error })}
    />
  );

  if (!looksLikeHtml(value)) {
    return (
      <>
        {textarea}
        {!!error && <div className="invalid-feedback">{error.message}</div>}
      </>
    );
  }

  // `base` makes the invitation's links open outside the frame, and the small
  // reset keeps the mail client's own styling off the border.
  const srcDoc = `<base target="_blank"><style>body{margin:12px;font-family:'Segoe UI',system-ui,sans-serif;font-size:14px;color:#242424;word-wrap:break-word}img{max-width:100%}</style>${value}`;

  return (
    <>
      <iframe
        title={t("meeting:meeting.form.description")}
        srcDoc={srcDoc}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        className="w-100 border rounded bg-white"
        style={{ height: 320 }}
      />
      <div className="mt-2">
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => setShowSource((current) => !current)}
        >
          {showSource
            ? t("meeting:meeting.form.hideSource")
            : t("meeting:meeting.form.showSource")}
        </button>
      </div>
      {/* Kept mounted but hidden: this is the field the form submits, and
          unmounting it would drop the description from the saved meeting. */}
      <div className={showSource ? "mt-2" : "d-none"}>{textarea}</div>
      {!!error && (
        <div className="invalid-feedback d-block">{error.message}</div>
      )}
    </>
  );
};

export default DescriptionPreview;
